import { Expression } from '../../builder/expression';
import { Result } from 'neverthrow';
import { TyStackTrace } from '../../type-checker/ty-stack-trace';
import { TokenMeta, WithMeta } from '../lex';
import {
  IdentToken,
  LiteralToken,
  OperatorToken,
  SymbolToken,
  TagToken,
} from '../lex/tokens';
import { Ty } from '../../ty';
import { AST } from '../../ast';
import { Relation } from '../../builder/relation';
import { Markup } from '../../builder/markup';
import { SpecialForm } from '../eraql';
import { WithRange } from '../lex/types';

export type Bindings = { readonly [name: string]: Item };

export type Item =
  | {
      readonly t: 'ty';
      readonly ty: Ty.ExtendedAttributeType;
    }
  | {
      readonly t: 'expr';
      readonly expr: Expression;
    }
  | {
      readonly t: 'order-by';
      readonly expr: Expression;
      readonly direction: AST.SortDirection;
    }
  | {
      readonly t: 'rel';
      readonly rel: Relation;
    }
  | {
      readonly t: 'mu';
      readonly mu: Markup;
    }
  | {
      readonly t: 'symbol-lookup';
      readonly lookup: SymbolLookup;
    }
  | {
      readonly t: 'item-map';
      readonly map: Record<string, CompileOutput>;
    }
  | {
      readonly t: 'item-list';
      readonly items: readonly CompileOutput[];
    }
  | ClosureWithBindings
  | SpecialFormItem;

export type SpecialFormItem = {
  readonly t: 'special-form';
  readonly sp: SpecialForm;
  readonly meta?: Record<string, string>;
};

export type ClosureWithBindings = {
  readonly t: 'closure-with-bindings';
  readonly closure: Closure;
  readonly bindings: Bindings;
};

export type CompileOutput = {
  readonly item: Item;
};

export type ParseError =
  | {
      readonly t:
        | 'cant-tag'
        | 'cant-pipe-into'
        | 'cant-call'
        | 'invalid-enum-variant'
        | 'unexpected-eof'
        | 'unexpected-token';
      readonly debug?: string;
    }
  | {
      readonly t: 'invalid-arity';
      readonly allowed?: number[];
      readonly got?: number;
    }
  | { readonly t: 'expected'; readonly expected: string | readonly string[] }
  | { readonly t: 'keyword-reserved'; readonly kw: string }
  | {
      readonly t: 'attribute-not-found';
      readonly name: string;
    }
  | {
      readonly t: 'unclosed-deliminator';
      readonly missing: string;
      readonly debug?: string;
    }
  | {
      readonly t: 'duplicate-key' | 'missing-key' | 'unexpected-key';
      readonly key: string;
    }
  | {
      readonly t: 'type-check-error';
      readonly trace: TyStackTrace;
    }
  | {
      readonly t: 'unbound-symbol';
      readonly sym: string;
      readonly bindings: Bindings;
    }
  | { readonly t: 'interrupt'; readonly val: any }
  | { readonly t: 'custom'; readonly msg: string }
  | { readonly t: 'stack'; readonly stack: readonly WithMeta<ParseError>[] };

export type ParseRes<T> = Result<T, readonly WithRange<ParseError>[]>;

export type AttrLookup = (
  name: string,
  meta: TokenMeta
) => Result<
  Ty.ExtendedAttributeType | Expression,
  readonly ParseError[]
> | null;

export type SymbolLookup = (
  sym: string,
  meta: TokenMeta
) => Result<Item, readonly ParseError[]> | null;

export type ParseCtx = {
  readonly eager?: boolean;
  readonly bindings?: { readonly [name: string]: Item };
  readonly dynamicSymbolLookup?: SymbolLookup;
  readonly offset?: number;
  readonly attributes?:
    | Relation
    | AttrLookup
    | Record<string, Ty.ExtendedAttributeType | Expression>;
};

export type AssocEntry =
  | {
      readonly t: 'assoc-kv';
      readonly key: WithMeta<string>;
      readonly val: WithMeta<EraQlAst>;
    }
  | {
      readonly t: 'assoc-spread';
      readonly val: WithMeta<EraQlAst>;
    };

/**
 * e.g.
 * ```
 * { foo: 1, bar: 2, ...rec, baz: 5 }
 * ```
 */
export type Assoc = {
  readonly t: 'assoc';
  readonly entries: readonly WithMeta<AssocEntry>[];
};

/**
 * e.g.
 * ```
 * [3, 1, 1, 0]
 * ```
 */
export type List = {
  readonly t: 'list';
  readonly elems: readonly WithMeta<EraQlAst>[];
};

/**
 * e.g.
 * ```
 * add(5, 6)
 *
 * 5 + 6
 *
 * hello @world
 * ```
 */
export type Call = {
  readonly t: 'call';
  readonly op: WithMeta<EraQlAst | OperatorToken | TagToken>;
  readonly args: readonly WithMeta<EraQlAst>[];
  readonly display: AST.FunctionCallDisplay;
};

/**
 * e.g.
 * ```
 * let x = 10; x + 5
 * ```
 */
export type Let = {
  readonly t: 'let';
  readonly sym: WithMeta<SymbolToken>;
  readonly bindingExpr: WithMeta<EraQlAst>;
  readonly body: WithMeta<EraQlAst>;
};

/**
 * e.g.
 * ```
 * |a, b| a + b
 * ```
 */
export type Closure = {
  readonly t: 'closure';
  readonly args: WithMeta<readonly { readonly sym: WithMeta<string> }[]>;
  readonly body: WithMeta<EraQlAst>;
};

/**
 * e.g.
 * ```
 * rel"attr"
 * ```
 */
export type AttrAccess = {
  readonly t: 'attr-access';
  readonly in: WithMeta<EraQlAst>;
  readonly attr: WithMeta<IdentToken>;
};

export type EraQlAst =
  | SymbolToken
  | LiteralToken
  | IdentToken
  | Call
  | Assoc
  | List
  | Let
  | AttrAccess
  | Closure;

/**
 * Pretty prints a ParseError with human-readable messages
 * @param error - The parse error to format
 * @returns A formatted string describing the error
 */
export const prettyPrintParseError = (error: ParseError): string => {
  switch (error.t) {
    case 'cant-tag':
      return 'Cannot tag this expression';

    case 'cant-pipe-into':
      return 'Cannot pipe into this expression';

    case 'cant-call':
      return 'Cannot call this expression';

    case 'invalid-enum-variant':
      return 'Invalid enum variant';

    case 'unexpected-eof':
      return 'Unexpected end of file';

    case 'unexpected-token': {
      // Check if the error has an 'original' field with the token text
      const errorWithOriginal = error as typeof error & { original?: string };
      if (errorWithOriginal.original !== undefined) {
        return `Unexpected token: "${errorWithOriginal.original}"`;
      }
      return 'Unexpected token';
    }

    case 'invalid-arity':
      if (error.allowed && error.got !== undefined) {
        return `Invalid arity: got ${error.got}, allowed: ${error.allowed.join(
          ', '
        )}`;
      } else if (error.allowed) {
        return `Invalid arity: allowed: ${error.allowed.join(', ')}`;
      } else if (error.got !== undefined) {
        return `Invalid arity: got ${error.got}`;
      }
      return 'Invalid arity';

    case 'expected': {
      const expectedStr = Array.isArray(error.expected)
        ? error.expected.join(', ')
        : error.expected;
      return `Expected: ${expectedStr}`;
    }

    case 'keyword-reserved':
      return `Keyword '${error.kw}' is reserved`;

    case 'attribute-not-found':
      return `Attribute not found: '${error.name}'`;

    case 'unclosed-deliminator':
      return `Unclosed delimiter, missing: '${error.missing}'`;

    case 'duplicate-key':
      return `Duplicate key: '${error.key}'`;

    case 'missing-key':
      return `Missing key: '${error.key}'`;

    case 'unexpected-key':
      return `Unexpected key: '${error.key}'`;

    case 'type-check-error':
      return `Type check error: ${error.trace.headline} - ${error.trace.message}`;

    case 'unbound-symbol':
      return `Unbound symbol: '${error.sym}'`;

    case 'interrupt':
      return `Interrupted: ${JSON.stringify(error.val)}`;

    case 'custom':
      return error.msg;

    case 'stack':
      return `Stack of errors (${error.stack.length}):\n${error.stack
        .map(
          ([err, meta], index) =>
            `  ${index + 1}. ${prettyPrintParseError(err)} at ${JSON.stringify(
              meta.range
            )}`
        )
        .join('\n')}`;

    default:
      return `Unknown parse error: ${JSON.stringify(error)}`;
  }
};
